import React, { useEffect, useRef, useState } from "react";
import HeaderTop from "../HeaderTop";
import Menus from "../Menus";
import Footers from "../Footers";
import "../../../libs/css/bootstrap.min3.3.7.css";
import "../../../libs/css/sweetalert2.min.css";
import { API_ROUTES } from "../../../utils/constants";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import HourlyLineChart from "./HourlyLineChart";

const networkData = [
  { id: "1", name: "MTN" },
  { id: "2", name: "GLO" },
  { id: "3", name: "AIRTEL" },
  { id: "4", name: "9MOBILE" },
];

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

function formatHour(datetime) {
  const timePart = datetime.split(" ")[1];
  const [h, m] = timePart.split(":");
  const hour = parseInt(h, 10);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${suffix}`;
}

export default function RevenueMetric(props) {
  const {
    objectToFormData,
    isAuthExpired,
    isLoading1,
    allCPs = [],
    userSessions = [],
  } = props;

  useEffect(() => {
    document.title = "Revenue Metric | Forthsoft";
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isFade, setIsFade] = useState(false);
  const [rawRevenue, setRawRevenue] = useState([]);
  const [rawCount, setRawCount] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [countChartData, setCountChartData] = useState([]);
  const [chartCompanies, setChartCompanies] = useState([]);
  const [selectedServiceName, setSelectedServiceName] = useState("");

  const userData = JSON.parse(localStorage.getItem("userDataFs"));

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      cp_id: "",
      network_id: "",
      date: getToday(),
      serviceId: "",
    },
  });

  const [serviceSearch, setServiceSearch] = useState("");
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [filteredServices, setFilteredServices] = useState([]);
  const serviceDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(e.target)
      ) {
        setShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let services =
      userSessions?.filter(
        (s) =>
          s.serviceName.toLowerCase().includes(serviceSearch.toLowerCase()) ||
          s.serviceId.toLowerCase().includes(serviceSearch.toLowerCase())
      ) || [];
    if (selectedNetwork) {
      services = services.filter(
        (s) => String(s.network_id) === String(selectedNetwork)
      );
    }
    setFilteredServices(services);
  }, [userSessions, serviceSearch, selectedNetwork]);

  const handleServiceSelect = (service) => {
    setServiceSearch(service.serviceName);
    setSelectedServiceName(service.serviceName);
    setValue("serviceId", service.serviceId);
    setShowServiceDropdown(false);
  };

  const handleServiceInputChange = (e) => {
    setServiceSearch(e.target.value);
    setShowServiceDropdown(true);
    if (e.target.value === "") {
      setSelectedServiceName("");
      setValue("serviceId", "");
    }
  };

  const getData = async (filters) => {
    setIsFade(true);
    setIsLoading(true);

    const body = { date: filters.date };
    if (filters.cp_id) body.cp_id = filters.cp_id;
    if (filters.network_id) body.network_id = filters.network_id;
    if (filters.serviceId) body.serviceId = filters.serviceId;

    try {
      const response = await fetch(API_ROUTES.HOURLY_CHART, {
        method: "POST",
        body: objectToFormData(body),
        headers: { token: userData?.responseDetails?.token },
      });
      const responseJSON = await response.json();
      isAuthExpired(responseJSON);

      if (responseJSON.status.toString() === "200") {
        const { hourly_revenue, hourly_count } = responseJSON.data;
        setRawRevenue(hourly_revenue || []);
        setRawCount(hourly_count || []);
        setServiceSearch("");
        setValue("serviceId", "");
        setIsLoading(false);
        setIsFade(false);
      } else {
        setRawRevenue([]);
        setRawCount([]);
        setRevenueChartData([]);
        setCountChartData([]);
        setChartCompanies([]);
        setSelectedServiceName("");
        setServiceSearch("");
        setValue("serviceId", "");
        setIsLoading(false);
        setIsFade(false);
        Swal.fire({
          title: "No Data",
          text: responseJSON.message || "No data found for the selected criteria",
          icon: "info",
        });
      }
    } catch (error) {
      console.log("Error getting data: ", error);
      setRawRevenue([]);
      setRawCount([]);
      setRevenueChartData([]);
      setCountChartData([]);
      setChartCompanies([]);
      setSelectedServiceName("");
      setServiceSearch("");
      setValue("serviceId", "");
      setIsLoading(false);
      setIsFade(false);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch data. Please try again.",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    if (!rawRevenue.length) {
      setRevenueChartData([]);
      setCountChartData([]);
      setChartCompanies([]);
      return;
    }

    const resolveCompany = (item) => {
      const cp = allCPs.find((c) => String(c.id) === String(item.company));
      return { ...item, company: cp ? cp.name : `CP ${item.company}`, time: formatHour(item.time) };
    };

    const resolvedRevenue = rawRevenue.map(resolveCompany);
    const resolvedCount = rawCount.map(resolveCompany);
    const companies = [...new Set(resolvedRevenue.map((i) => i.company))];

    const revenueGrouped = {};
    resolvedRevenue.forEach((item) => {
      if (!revenueGrouped[item.time]) revenueGrouped[item.time] = { time: item.time };
      revenueGrouped[item.time][item.company] = Number(item.chargeAmount);
    });

    const countGrouped = {};
    resolvedCount.forEach((item) => {
      if (!countGrouped[item.time]) countGrouped[item.time] = { time: item.time };
      countGrouped[item.time][item.company] = Number(item.subscriber_count);
    });

    setRevenueChartData(Object.values(revenueGrouped));
    setCountChartData(Object.values(countGrouped));
    setChartCompanies(companies);
  }, [rawRevenue, rawCount, allCPs]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getData({ date: getToday(), cp_id: "", network_id: "", serviceId: "" });
  }, []);

  return (
    <>
      <div className="flapt-page-wrapper mainContent">
        <Menus />

        <div className="flapt-page-content">
          <HeaderTop />

          <div className="main-content introduction-farm mt--20">
            <div className="content-wraper-area">
              <div className="dashboard-area">
                <div className="container-fluid mb-90">
                  <div className="card-header mb--30 mb-xs-10 ml-10">
                    <h2>Revenue Metric</h2>
                  </div>

                  {isLoading1 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <div className="row mb-10 mt-40">
                        <form onSubmit={handleSubmit(getData)} className="row">
                          <div className="col-lg-12">
                            <div className="col-sm-3 pr-0">
                              <select
                                className="filter_text"
                                {...register("cp_id")}
                                style={{ textTransform: "capitalize" }}
                              >
                                <option value="">-Select CP-</option>
                                {allCPs.map((cp) => (
                                  <option key={cp.id} value={cp.id}>
                                    {cp.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-sm-2 pr-0">
                              <select
                                className="filter_text"
                                {...register("network_id")}
                                style={{ textTransform: "capitalize" }}
                                onChange={(e) =>
                                  setSelectedNetwork(e.target.value)
                                }
                              >
                                <option value="">-Select Network-</option>
                                {networkData.map((n) => (
                                  <option key={n.id} value={n.id}>
                                    {n.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div
                              className="col-sm-3 pr-0"
                              ref={serviceDropdownRef}
                            >
                              <div style={{ position: "relative" }}>
                                <input
                                  type="text"
                                  className="filter_text"
                                  placeholder="Search Service (optional)..."
                                  value={serviceSearch}
                                  onChange={handleServiceInputChange}
                                  onFocus={() => setShowServiceDropdown(true)}
                                  style={{ textTransform: "capitalize" }}
                                  autoComplete="off"
                                />
                                <input
                                  type="hidden"
                                  {...register("serviceId")}
                                />
                                {showServiceDropdown && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      backgroundColor: "white",
                                      border: "1px solid #ddd",
                                      borderRadius: "4px",
                                      zIndex: 1000,
                                      marginTop: "2px",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    {filteredServices.length > 0 ? (
                                      filteredServices.map((service) => (
                                        <div
                                          key={service.serviceId}
                                          onClick={() =>
                                            handleServiceSelect(service)
                                          }
                                          style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #f0f0f0",
                                            textTransform: "capitalize",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              "#f5f5f5")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                              "white")
                                          }
                                        >
                                          {service.serviceName}
                                        </div>
                                      ))
                                    ) : (
                                      <div
                                        style={{
                                          padding: "8px 12px",
                                          color: "#999",
                                        }}
                                      >
                                        No services found
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-sm-2 pr-0">
                              <input
                                type="date"
                                className="filter_text"
                                {...register("date", {
                                  required: "Date is required",
                                })}
                                max={getToday()}
                              />
                              {errors.date && (
                                <small className="text-danger">
                                  {errors.date.message}
                                </small>
                              )}
                            </div>

                            <div className="col-sm-2 p-0 pl-5">
                              <button
                                className="btn_1 btn-primary w-100"
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                  opacity: isSubmitting ? "0.5" : 1,
                                  minHeight: "38px",
                                }}
                              >
                                {isSubmitting ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-2"></i>
                                    Loading...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-search mr-2"></i>
                                    Filter
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>

                      {selectedServiceName && (
                        <div
                          className="text-left pl-5 mt-5 mb-5"
                          style={{ fontSize: "14px", color: "#555" }}
                        >
                          Selected Service:{" "}
                          <strong style={{ textTransform: "capitalize" }}>
                            {selectedServiceName}
                          </strong>
                        </div>
                      )}

                      <div style={{ opacity: isFade ? "0.3" : 1 }}>
                        {isLoading && <LoadingSpinner />}

                        {!isLoading && revenueChartData.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "32px",
                              marginTop: "24px",
                            }}
                          >
                            <HourlyLineChart
                              title="Hourly Revenue"
                              data={revenueChartData}
                              companies={chartCompanies}
                              yWidth={100}
                              yFormatter={(v) => "₦" + v.toLocaleString()}
                              tooltipFormatter={(value) =>
                                "₦" + Number(value).toLocaleString()
                              }
                            />
                            <HourlyLineChart
                              title="Hourly Subscriber Count"
                              data={countChartData}
                              companies={chartCompanies}
                              yWidth={80}
                              yFormatter={(v) => v.toLocaleString()}
                              tooltipFormatter={(value) =>
                                Number(value).toLocaleString()
                              }
                            />
                          </div>
                        )}

                        {!isLoading && revenueChartData.length === 0 && (
                          <div
                            className="mt-20 ml-10"
                            style={{ color: "#888", fontSize: "14px" }}
                          >
                            Use the filter above to load revenue metrics.
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Footers />
          </div>
        </div>
      </div>
    </>
  );
}
